/**
 * A FORMA do launcher em repouso — e quem decide qual é: o APARELHO (DEV-224).
 *
 * O DEV-222 introduziu o talo (o launcher encolhido numa pílula fina, sempre tocável) para
 * dar um caminho de volta a quem esconde no celular, onde a tecla `?` não existe. Mas ele
 * pendurou a escolha da forma no HOST (`data-altbug-hotkey`), e o host não sabe em que
 * aparelho a página abriu: o resultado é o talo aparecendo no desktop — onde a volta é o
 * atalho e esconder tem de esconder — e o celular oscilando entre o menu aberto e o talo,
 * sem nunca ter a bolinha.
 *
 * Agora cada aparelho tem o seu par de estados, e só ele:
 *
 * - **Desktop** (`hover` disponível): **bolinha** ou **sumido**. O menu abre no hover, o
 *   atalho `?` e a resposta nova (DEV-176) trazem o botão de volta, e o diálogo de esconder
 *   pergunta se a escolha vale para o próximo load.
 * - **Celular** (`hover: none`): **talo** ou **sumido**. O talo é o repouso, não o castigo —
 *   grudado numa lateral, arrastável na vertical, com badge e balão como qualquer launcher.
 *   Um toque nele abre o MESMO menu da bolinha. Esconder ali vale só para a sessão: no
 *   celular não há atalho de teclado para desfazer, então a volta é sempre o próximo load.
 *
 * O `hotkeyDoHost` some da conta da forma e vira o que sempre foi na prática: o launcher
 * **nasce** oculto naquele embed (ver `nasceVisivel`). Escondido é escondido nos dois
 * aparelhos — o que muda é o que traz de volta.
 *
 * Vive em módulo PURO porque é a regra, e regra do widget precisa de teste: `widget.ts` não
 * tem cobertura e o shadow é `mode: "closed"` — o mesmo motivo de `chamados/badge.ts`.
 */

export type FormaDoLauncher = "cheio" | "talo" | "sumido";

/**
 * Em que forma o launcher aparece agora.
 *
 * `menuAberto` é o toque no talo: no celular não há hover, então o estado que o CSS pinta
 * sozinho no desktop precisa ser DITO aqui — e o que ele diz é que a forma volta a ser a
 * cheia, com o menu forçado aberto por classe. É por isso que ele não é um terceiro valor
 * do enum: o launcher aberto no dedo é o mesmo launcher aberto no mouse, e um valor próprio
 * duplicaria toda a pintura.
 */
export function formaDoLauncher(visivel: boolean, toque: boolean, menuAberto: boolean): FormaDoLauncher {
  if (!visivel) return "sumido";
  if (!toque) return "cheio";

  return menuAberto ? "cheio" : "talo";
}

/**
 * O launcher está na tela quando o widget monta?
 *
 * Duas negativas, e elas não são simétricas: `data-altbug-hotkey` é do HOST e vale em
 * qualquer aparelho (o site declarou que não quer botão nenhum), enquanto o "manter
 * escondido" é da PESSOA e só existe no desktop. No celular esconder vale para a sessão —
 * não há `?` para desfazer —, então um `altbug:oculto` gravado numa visita de desktop (mesmo
 * relator, outro aparelho, mesma chave por token) não pode fazer o launcher nascer sumido
 * no dedo, onde ele ficaria sem caminho de volta.
 */
export function nasceVisivel(hotkeyDoHost: boolean, ocultoGravado: boolean, toque: boolean): boolean {
  if (hotkeyDoHost) return false;

  return toque || !ocultoGravado;
}

/**
 * "Esconder" abre o diálogo do "reaparecer quando recarregar?", ou esconde na hora?
 *
 * Só o desktop pergunta, porque só lá a resposta "não" tem como ser desfeita: o `?` traz o
 * botão de volta na hora. No celular a pergunta ofereceria um caminho sem volta — e a
 * resposta certa seria sempre a mesma —, então o gesto é um só e vale até o próximo load.
 */
export function perguntaAoEsconder(toque: boolean): boolean {
  return !toque;
}

/**
 * O tick de fundo (15s) deve repintar o repouso?
 *
 * A pergunta existe porque o ramo escondido deixou de ser `innerHTML = ""`: repintar um nó
 * visível e focável o destrói e recria, o que tira o foco de quem chegou nele pelo teclado e
 * reinicia a transição no meio. Então só repinta o que muda na tela — a FORMA, sempre, e a
 * contagem quando há launcher para mostrá-la.
 *
 * O talo entrou nesse "há launcher" no DEV-224: ele passou a ser o repouso do celular, e
 * repouso sem badge é notificação que não chega. O foco que o repaint custa é o do teclado,
 * que no aparelho do talo quase não existe — e a badge é o que a pessoa foi ver.
 */
export function deveRepintarRepouso(
  antes: FormaDoLauncher,
  depois: FormaDoLauncher,
  novasAntes: number,
  novasDepois: number,
): boolean {
  if (antes !== depois) return true;

  return depois !== "sumido" && novasAntes !== novasDepois;
}

/** O que o talo diz a quem chega nele por leitor de tela ou pelo `title` do hover. */
export const ROTULO_TALO = "Abrir o menu de suporte";
